import './CartCampaignSingleOfferBuyXGetYFlattenedSkus.scss';

import { CartSkuApiDto, OfferApiDto, OfferCampaignApiDto } from '@b2x/storefront-api-js-client/src';
import classNames from 'classnames';
import { useEffect, useMemo, useRef } from 'react';

import { Accordion, AccordionItem } from './Accordion';
import { BlankButton } from './BlankButton';
import { Button } from './Button';
import { CartSku } from './CartSku';
import { Col } from './Col';
import { appConfig } from './config';
import { EqualHeight } from './EqualHeight';
import { EqualHeightElement } from './EqualHeightElement';
import { Div, Span } from './HTMLElement';
import { t } from './i18n/i18n';
import { Icon } from './Icon';
import { ImageSwitcher } from './ImageSwitcher';
import { Loading } from './Loading';
import { Modal, ModalBody, ModalHeader } from './Modal';
import { Portal } from './Portal';
import { Row } from './Row';
import { Tabs } from './Tabs';
import {
  PickerSku,
  useCartCampaignSingleOfferBuyXGetYFlattenedSkus,
} from './useCartCampaignSingleOfferBuyXGetYFlattenedSkus';
import { useModalByRef } from './useModal';
import { formatCurrency, formatHtml, uiClassName } from './util';

export interface CartCampaignSingleOfferBuyXGetYFlattenedSkusProps {
  campaign: OfferCampaignApiDto;
  campaignsCartSkus: Partial<Record<string, Array<CartSkuApiDto>>> | undefined;
  showCampaignOfferOnMount?: boolean;
}

interface CollapsibleCampaignSectionProps {
  campaign: OfferCampaignApiDto;
  campaignCartSkus?: Array<CartSkuApiDto>;
  isCollapsible: boolean;
  offer: OfferApiDto;
  showPickerModal(): void;
}

const CollapsibleCampaignSection = ({
  campaign,
  campaignCartSkus,
  isCollapsible,
  offer,
  showPickerModal,
}: CollapsibleCampaignSectionProps) => {
  useEffect(() => {
    // Ho dovuto gestire così il click dell'edit per evitare che l'accordion cambi di stato, bootstrap ascolta su document e react in bubble
    const handleEditClickCapture = (event: MouseEvent) => {
      const target = event.target;
      if (
        !(target instanceof Element) ||
        !target.closest(`#cart-campaign-buyxgety-${campaign.id} [data-cart-campaign-edit]`)
      ) {
        return;
      }
      event.stopImmediatePropagation();
      showPickerModal();
    };
    window.addEventListener('click', handleEditClickCapture, true);
    return () => window.removeEventListener('click', handleEditClickCapture, true);
  }, [showPickerModal, campaign.id]);

  return (
    <Accordion
      className="cart-campaign-single-offer-buyxgety-flattened-skus-accordion"
      id={`cart-campaign-buyxgety-${campaign.id}`}
      itemBodyClassName="pt-0"
      itemButtonClassName="bg-transparent shadow-none"
      itemClassName="bg-transparent border-0"
      itemHeaderClassName="w-100"
      withoutXPadding
    >
      <AccordionItem
        id={`offer-${campaign.id}`}
        title={
          <CampaignHeader
            campaignCartSkus={campaignCartSkus}
            isCollapsible={isCollapsible}
            offer={offer}
            showPickerModal={showPickerModal}
          />
        }
      >
        <CampaignBody campaignCartSkus={campaignCartSkus} isCollapsible={isCollapsible} />
      </AccordionItem>
    </Accordion>
  );
};

interface CampaignHeaderProps {
  campaignCartSkus?: Array<CartSkuApiDto>;
  isCollapsible: boolean;
  offer: OfferApiDto;
  showPickerModal(): void;
}

const CampaignHeader = ({ campaignCartSkus, isCollapsible, offer, showPickerModal }: CampaignHeaderProps) => {
  const buyxgetyText = appConfig.cartCampaigns?.buyxgety?.text;

  const text =
    buyxgetyText?.type === 'dynamic' ? (
      <>
        {(offer.yQuantityOnCart ?? 0) > 0
          ? t('cart.cartCampaign.selectedFreeSample', {
              count: offer.yQuantityOnCart,
              total: offer.yQuantity,
            })
          : t('cart.cartCampaign.selectFreeSample', { count: offer.yQuantity })}
        {buyxgetyText.icon && <Icon className={uiClassName({ bs5: 'ms-1' })} name={buyxgetyText.icon} size={24} />}
      </>
    ) : (
      offer.name
    );

  return (
    <Div display="flex" gap={3} justifyContent="spaceBetween">
      <BlankButton data-cart-campaign-edit onClick={showPickerModal}>
        {appConfig.icons?.cartCampaign && (
          <Span className="icon" paddingEnd={2}>
            <Icon name={appConfig.icons.cartCampaign} />
          </Span>
        )}
        <Span className={classNames('name', { small: isCollapsible })}>{text}</Span>
      </BlankButton>
      {campaignCartSkus && campaignCartSkus.length > 0 && (
        <BlankButton data-cart-campaign-edit onClick={showPickerModal}>
          {appConfig.icons?.cartCampaign && (
            <Span className="icon" paddingEnd={2}>
              <Icon name={appConfig.icons.cartCampaign} />
            </Span>
          )}
          <Span className="edit">{t('misc.edit')}</Span>
        </BlankButton>
      )}
    </Div>
  );
};

const CampaignBody = ({
  campaignCartSkus,
  isCollapsible,
}: {
  campaignCartSkus?: Array<CartSkuApiDto>;
  isCollapsible: boolean;
}) => {
  if (!campaignCartSkus || campaignCartSkus.length === 0) {
    return null;
  }

  return (
    <Div background="white">
      {campaignCartSkus.map((campaignCartSku, index) => (
        <Div
          className="cart-campaign-single-offer-buyxgety-flattened-sku"
          key={campaignCartSku.id}
          marginTop={isCollapsible ? 2 : 4}
          paddingX={3}
        >
          <CartSku
            cartSku={campaignCartSku}
            index={index}
            temporaryPropToDisableProductPageLink
            temporaryPropToDisableWishlistButton
          />
        </Div>
      ))}
    </Div>
  );
};

export const CartCampaignSingleOfferBuyXGetYFlattenedSkus = ({
  campaign,
  campaignsCartSkus,
  showCampaignOfferOnMount,
}: CartCampaignSingleOfferBuyXGetYFlattenedSkusProps) => {
  const {
    addToCartButtonEnabled,
    campaignCartSkus,
    handleAddToCartButtonClick,
    handlePickerSkuButtonClick,
    isLoadingSelectedSkuIds,
    offer,
    pickerSkus,
    selectedSkusIds,
  } = useCartCampaignSingleOfferBuyXGetYFlattenedSkus({
    campaign,
    campaignsCartSkus,
  });
  const [pickerModalRef, showPickerModal, closePickerModal] = useModalByRef('modal');

  const isSelectedSkusVerified = useRef(false);

  useEffect(() => {
    if (
      !showCampaignOfferOnMount ||
      isSelectedSkusVerified.current ||
      isLoadingSelectedSkuIds ||
      offer.problemType !== undefined
    ) {
      return;
    }

    isSelectedSkusVerified.current = true;
    if (!selectedSkusIds.length) {
      showPickerModal();
    }
  }, [selectedSkusIds.length, showPickerModal, isLoadingSelectedSkuIds, showCampaignOfferOnMount, offer.problemType]);

  const isCollapsible = !!appConfig.cartCampaigns?.collapsible;
  const showCollapsibleCampaignSection = isCollapsible && !!campaignCartSkus?.length;

  return (
    <>
      {offer.problemType === undefined ? (
        <>
          <Div
            className="cart-campaign-single-offer-buyxgety-flattened-skus"
            paddingX={4}
            paddingY={showCollapsibleCampaignSection ? 2 : 4}
          >
            {showCollapsibleCampaignSection ? (
              <CollapsibleCampaignSection
                campaign={campaign}
                campaignCartSkus={campaignCartSkus}
                isCollapsible={isCollapsible}
                offer={offer}
                showPickerModal={showPickerModal}
              />
            ) : (
              <>
                <CampaignHeader
                  campaignCartSkus={campaignCartSkus}
                  isCollapsible={isCollapsible}
                  offer={offer}
                  showPickerModal={showPickerModal}
                />
                <CampaignBody campaignCartSkus={campaignCartSkus} isCollapsible={false} />
              </>
            )}
          </Div>
          <Portal>
            <EqualHeight>
              <Modal
                className="cart-campaign-single-offer-buyxgety-flattened-skus-picker"
                innerRef={pickerModalRef}
                size="extra-large"
              >
                <ModalHeader title="" />
                <ModalBody
                  className={uiClassName({
                    bs5: 'pt-0 text-center',
                  })}
                >
                  <Div className="title" marginBottom={1}>
                    {offer.name}
                  </Div>
                  <Div className="subtitle" marginBottom={3}>
                    {t('misc.productsPicker.progress', { count: selectedSkusIds.length, total: offer.yQuantity })}
                  </Div>

                  {appConfig.cartCampaigns?.productsByCategoryTabs ? (
                    <PickerSkusPerCategoryTabs
                      handlePickerSkuButtonClick={handlePickerSkuButtonClick}
                      pickerSkus={pickerSkus}
                    />
                  ) : (
                    <PickerSkusList handlePickerSkuButtonClick={handlePickerSkuButtonClick} pickerSkus={pickerSkus} />
                  )}
                  <Div className="add-to-cart-button" marginBottom={3}>
                    <Button
                      disabled={!addToCartButtonEnabled}
                      label={t('misc.addToCart')}
                      // eslint-disable-next-line react/jsx-no-bind
                      onClick={() => {
                        handleAddToCartButtonClick()
                          .then(() => {
                            closePickerModal();
                          })
                          .catch((error) => {
                            // eslint-disable-next-line no-console
                            console.error('CartCampaign add to cart failed', error);
                          });
                      }}
                    />
                  </Div>
                </ModalBody>
              </Modal>
            </EqualHeight>
          </Portal>
        </>
      ) : (
        <>
          {offer.problemType === 'MIN_TOT_CART' && offer.minProblemValue !== undefined && offer.minProblemValue > 0 && (
            <>
              <Div className="cart-campaign-single-offer-buyxgety-flattened-skus" padding={4}>
                {appConfig.icons?.cartCampaign && (
                  <Span className="icon" paddingEnd={2}>
                    <Icon name={appConfig.icons.cartCampaign} />
                  </Span>
                )}
                <Span className="">
                  {formatHtml(
                    t('cart.cartCampaign.toFreeSample', {
                      toFreeSampleAmount: formatCurrency(offer.minProblemValue - offer.calculatedCartSubtotal),
                    })
                  )}
                </Span>
              </Div>
            </>
          )}
        </>
      )}
    </>
  );
};

type PickerSkusPerCategoryTabsProps = {
  handlePickerSkuButtonClick(skuId: string): void;
  pickerSkus?: Array<PickerSku>;
};

const PickerSkusPerCategoryTabs = ({ handlePickerSkuButtonClick, pickerSkus }: PickerSkusPerCategoryTabsProps) => {
  const tabsData = useMemo(() => {
    const categories: Set<string> = new Set();
    pickerSkus?.forEach((pickerSku) => {
      const category = pickerSku.product.pages?.at(0)?.name;
      if (category) {
        categories.add(category);
      }
    });

    return Array.from(categories)
      .map((tab) => ({
        products: pickerSkus?.filter((p) => p.product.pages?.at(0)?.name === tab),
        title: tab.toUpperCase(),
      }))
      .sort((a, b) => (b.products?.length ?? 0) - (a.products?.length ?? 0));
  }, [pickerSkus]);

  if (!pickerSkus) {
    return (
      <Div textAlign="center">
        <Loading color="black" />
      </Div>
    );
  }

  return (
    <Tabs
      direction="horizontal"
      horizontalAlignment="center"
      initialActiveTab={0}
      tabStyle="tabs"
      tabs={tabsData.map((tab) => ({
        children: <PickerSkusList handlePickerSkuButtonClick={handlePickerSkuButtonClick} pickerSkus={tab.products} />,
        title: tab.title,
      }))}
    />
  );
};

type PickerSkusListProps = {
  handlePickerSkuButtonClick(skuId: string): void;
  pickerSkus?: Array<PickerSku>;
};

const PickerSkusList = ({ handlePickerSkuButtonClick, pickerSkus }: PickerSkusListProps) => {
  return (
    <Div className="skus" marginBottom={5} marginTop={3}>
      {pickerSkus ? (
        <Row cols={{ lg: 4, xs: 2 }} gapX={3} gapY={5}>
          {pickerSkus.map((pickerSku, index) => (
            <Col key={pickerSku.id}>
              <Div className={classNames('sku', { selected: pickerSku.selected })}>
                <Div className="image-container" marginBottom={2}>
                  <EqualHeightElement colIndex={index} name="image">
                    <ImageSwitcher
                      {...pickerSku.image}
                      aspectRatio={appConfig.productImageAspectRatio}
                      fluid
                      format={800}
                    />
                  </EqualHeightElement>
                </Div>
                <Div className="name" marginBottom={2}>
                  <EqualHeightElement colIndex={index} name="name">
                    {pickerSku.product.name && <div>{pickerSku.product.name}</div>}
                    {pickerSku.name && <div>{pickerSku.name}</div>}
                  </EqualHeightElement>
                </Div>
                <Div display="grid">
                  <Button
                    className="select"
                    disabled={!pickerSku.buttonEnabled}
                    label={pickerSku.selected ? t('misc.productsPicker.selected') : t('misc.productsPicker.select')}
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={() => {
                      handlePickerSkuButtonClick(pickerSku.id);
                    }}
                    variant="plain"
                  />
                </Div>
              </Div>
            </Col>
          ))}
        </Row>
      ) : (
        <Div textAlign="center">
          <Loading color="black" />
        </Div>
      )}
    </Div>
  );
};
